/**
 * Password sign-in, browser side (PLAN.md 4.14). The browser stretches the password with
 * PBKDF2-SHA256, 600 000 iterations (OWASP 2023), and sends only the result. The server hashes
 * that once more with a random salt (apps/api/src/password.ts). Workers' WebCrypto refuses more
 * than 100 000 PBKDF2 iterations and has little CPU time, so the expensive part runs here.
 *
 * The salt is the login address, so the same password gives a different value for every member,
 * and the server never sees the password itself. A password only signs in: it does not open the
 * member's key, which stays behind passkey + PIN.
 */

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MAX_LENGTH = 256;
export const PASSWORD_STRETCH_ITERATIONS = 600_000;
const SALT_PREFIX = "vrelis login v1|";

export type PasswordProblem = "too_short" | "too_long";

export function passwordProblem(password: string): PasswordProblem | null {
  const n = [...password.normalize("NFKC")].length;
  if (n < PASSWORD_MIN_LENGTH) return "too_short";
  if (n > PASSWORD_MAX_LENGTH) return "too_long";
  return null;
}

export function normalizeLoginEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** 64 hex characters: what the browser sends instead of the password. */
export async function stretchPassword(password: string, email: string, iterations = PASSWORD_STRETCH_ITERATIONS): Promise<string> {
  const problem = passwordProblem(password);
  if (problem) throw new Error(`password ${problem}`);
  const subtle = (globalThis as { crypto?: Crypto }).crypto!.subtle;
  const enc = new TextEncoder();
  const key = await subtle.importKey("raw", enc.encode(password.normalize("NFKC")), "PBKDF2", false, ["deriveBits"]);
  const bits = await subtle.deriveBits(
    { name: "PBKDF2", hash: "SHA-256", salt: enc.encode(SALT_PREFIX + normalizeLoginEmail(email)), iterations },
    key,
    256,
  );
  return [...new Uint8Array(bits)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
